/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../../core/components';
import { Component } from '@angular/core';
import { ErrorResponseModel } from '../../../../../../core/models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LogService, RbacService, UtilsService } from '../../../../../../core/services';
import { Observable } from 'rxjs';
import { SNMPConfigurationApiService } from '../../../../shared/http';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-snmp-config',
    templateUrl: './snmp-config.component.html',
    styles: [`
        .versions {
            display: flex;
            justify-content: flex-start;
            gap: 10%;
        }
    `]
})
export class SnmpConfigComponent extends BaseComponent {
    /**
     * Stored form
     */
    public formGroup: FormGroup;

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    /**
     * Stored form values
     * @private
     */
    private storedState: boolean = false;

    constructor(
        private activatedRoute: ActivatedRoute,
        private formBuilder: FormBuilder,
        private logService: LogService,
        private rbacService: RbacService,
        private router: Router,
        private snmpConfigApiService: SNMPConfigurationApiService,
        private utilsService: UtilsService
    ) {
        super();

        this.setUpForm();
        this.setUpFormSubscribers();
        this.setUpPageData();
        this.registerButtons();
    }

    /**
     * Post time zone to server.
     */
    public submit(): Observable<any> {
        return new Observable((observer) => {
            this.snmpConfigApiService.setSNMP(this.formGroup.value)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.router.navigate(['../'], { relativeTo: this.activatedRoute }).catch();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Set up the form.
     * @private
     */
    private setUpForm(): void {
        this.formGroup = this.formBuilder.group({
            enabledV2: [false],
            enabledV3: [false],
            community: [''],  // v2
            location: [null, Validators.required], // (either or both)
            contact: [null, Validators.required], // (either or both)
        });
    }

    /**
     * Set up form subscribers.
     * @private
     */
    private setUpFormSubscribers(): void {
        this.formGroup.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (values: boolean): void => {
                    this.utilsService.debounce('form', (): void => {
                        if (this.storedState !== values['enabledV2'] || values['enabledV3']) {
                            this.storedState = values['enabledV2'] || values['enabledV3'];
                            this.toggleFieldsState(this.storedState);
                        }
                    });
                }
            });
    }

    /**
     * Toggle fields state.
     * @param state
     * @private
     */
    private toggleFieldsState(state: boolean): void {
        const status: 'enable' | 'disable' = state ? 'enable' : 'disable';
        this.formGroup.get('community')[status]({ emitEvent: false });
        this.formGroup.get('location')[status]({ emitEvent: false });
        this.formGroup.get('contact')[status]({ emitEvent: false });
    }

    /**
     * Set up page data.
     * @private
     */
    private setUpPageData(): void {
        this.snmpConfigApiService.getSNMP()
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (config: any): void => {
                    this.formGroup.patchValue(config);
                },
                error: (error: any): void => {
                    this.logService.error('SNMPConfigComponent: setUpPageData failed to fetch data ', error);
                }
            });
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'snmp-config-update': [{ path: '/snmp', methods: ['PUT'] }]
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);

        if (!this.permissionsLookup['snmp-config-update']) {
            this.formGroup.disable();
        }
    }
}
